import type { JobRunCountsDto, JobRunSummaryDto, JobStepDto } from '@/api/generated/schemas';

/**
 * Čisté odvozování jobs UI z backendových DTO (port logiky design návrhu „Joby na pozadí"):
 * mapování stavů BE → vizuální stavy, segmentované progress bary, roll-up skupin a stavba
 * stromu kroků (indent guides, elbow konektory, chunking >300 dětí po 100).
 */

/** Vizuální stav uzlu/jobu dle designu (Ve frontě / Běží / Dokončeno / Chyba). */
export type JobVisualStatus = 'queued' | 'running' | 'done' | 'error';

/** Vizuální stav skupiny — navíc „waiting" (něco hotovo, nic neběží; ikona hodin). */
export type GroupVisualStatus = JobVisualStatus | 'waiting';

/** Segment progress baru: poměrná šířka + barevný tón. */
export interface BarSegment {
  grow: number;
  tone: JobVisualStatus;
}

/** Nad tolik listů se segmenty agregují do max. 4 proporcionálních bloků (dle designu). */
const MAX_PER_LEAF_SEGMENTS = 120;

/** Nad tolik dětí jednoho rodiče se strom dělí do syntetických skupin po 100 (dle designu). */
const CHUNK_THRESHOLD = 300;
const CHUNK_SIZE = 100;

/** Mapování BE stavu kroku na vizuální stav (Blocked i Ready = „Ve frontě"; Skipped = done s „—"). */
export function stepVisualStatus(status: string): JobVisualStatus {
  switch (status) {
    case 'Running':
      return 'running';
    case 'Succeeded':
    case 'Skipped':
      return 'done';
    case 'Failed':
      return 'error';
    default:
      return 'queued';
  }
}

/** Počet dokončených listů (Succeeded + Skipped). */
export function countsDone(counts: JobRunCountsDto): number {
  return counts.succeeded + counts.skipped;
}

/** Vizuální stav celého jobu z jeho souhrnu (pořadí dle designu: error → done → running → queued). */
export function runVisualStatus(run: JobRunSummaryDto): JobVisualStatus {
  if (run.status === 'Failed' || run.counts.failed > 0) return 'error';
  if (run.status === 'Succeeded') return 'done';
  const done = countsDone(run.counts);
  if (run.counts.total > 0 && done === run.counts.total) return 'done';
  if (run.counts.running > 0 || done > 0) return 'running';
  return 'queued';
}

/** Agregované segmenty z počtů (pořadí done → running → error → queued, jen nenulové). */
export function segmentsFromCounts(counts: JobRunCountsDto): BarSegment[] {
  const parts: BarSegment[] = [
    { tone: 'done', grow: countsDone(counts) },
    { tone: 'running', grow: counts.running },
    { tone: 'error', grow: counts.failed },
    { tone: 'queued', grow: counts.blocked + counts.ready },
  ];
  return parts.filter((p) => p.grow > 0);
}

/** Segmenty z konkrétních listů: ≤120 → 1 segment/list ve stromovém pořadí, jinak agregace. */
export function segmentsFromLeaves(leaves: readonly JobStepDto[]): BarSegment[] {
  if (leaves.length <= MAX_PER_LEAF_SEGMENTS) {
    return leaves.map((leaf) => ({ tone: stepVisualStatus(leaf.status), grow: 1 }));
  }
  const tally: Record<JobVisualStatus, number> = { queued: 0, running: 0, done: 0, error: 0 };
  for (const leaf of leaves) {
    tally[stepVisualStatus(leaf.status)] += 1;
  }
  const parts: BarSegment[] = [
    { tone: 'done', grow: tally.done },
    { tone: 'running', grow: tally.running },
    { tone: 'error', grow: tally.error },
    { tone: 'queued', grow: tally.queued },
  ];
  return parts.filter((p) => p.grow > 0);
}

/** Roll-up vizuálního stavu skupiny z jejích list-potomků (dle design skriptu). */
export function groupVisualStatus(leaves: readonly JobStepDto[]): GroupVisualStatus {
  let done = 0;
  let running = 0;
  let error = 0;
  for (const leaf of leaves) {
    const visual = stepVisualStatus(leaf.status);
    if (visual === 'done') done += 1;
    else if (visual === 'running') running += 1;
    else if (visual === 'error') error += 1;
  }
  if (error > 0) return 'error';
  if (done === leaves.length && leaves.length > 0) return 'done';
  if (running > 0) return 'running';
  if (done > 0) return 'waiting';
  return 'queued';
}

/** Listy (pracovní kroky) v dokumentovém pořadí stromu (DFS dle SortOrder) — pro celkový bar. */
export function leavesInDocumentOrder(steps: readonly JobStepDto[]): JobStepDto[] {
  const childrenByParent = new Map<string | null, JobStepDto[]>();
  for (const step of steps) {
    const parentKey = step.parentStepId ?? null;
    const list = childrenByParent.get(parentKey);
    if (list) list.push(step);
    else childrenByParent.set(parentKey, [step]);
  }
  for (const list of childrenByParent.values()) {
    list.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  const leaves: JobStepDto[] = [];
  const walk = (parentId: string | null) => {
    for (const child of childrenByParent.get(parentId) ?? []) {
      if (child.isGroup) walk(child.id);
      else leaves.push(child);
    }
  };
  walk(null);
  return leaves;
}

/** Informace o čekání listu: na které předchůdce (Blocked), nebo na volný slot (Ready). */
export type LeafWaitInfo =
  | { kind: 'dependencies'; names: string[]; more: number }
  | { kind: 'slot' };

/** Řádek stromu detailu jobu (view model pro `JobStepTree`). */
export type JobTreeRow = JobTreeGroupRow | JobTreeLeafRow;

interface JobTreeRowBase {
  key: string;
  /** Svislé vodicí linky předků (true = linka pokračuje). */
  guides: boolean[];
  /** Elbow konektor před uzlem (vrstvy pod kořenem). */
  hasConnector: boolean;
  /** Svislá část konektoru pokračuje dolů (uzel není poslední sourozenec). */
  connectorContinues: boolean;
}

export interface JobTreeGroupRow extends JobTreeRowBase {
  kind: 'group' | 'chunk';
  name: string;
  visual: GroupVisualStatus;
  collapsed: boolean;
  /** Automatické sbalení odvozené z aktuálního stavu (rozbalené = rozpracované/chybové). */
  defaultCollapsed: boolean;
  maxParallelism: number | null;
  segments: BarSegment[];
  doneCount: number;
  totalCount: number;
  /** Syntetický chunk nese vlastní název „Kroky X – Y" přes i18n — from/to jen pro něj. */
  chunkRange?: { from: number; to: number };
}

export interface JobTreeLeafRow extends JobTreeRowBase {
  kind: 'leaf';
  step: JobStepDto;
  visual: JobVisualStatus;
  wait: LeafWaitInfo | null;
}

/**
 * Automatické sbalení skupiny z jejího stavu: otevřené zůstávají jen rozpracované (běží / něco
 * hotovo a čeká na slot) a chybové skupiny; zafronténé a úspěšně dokončené jsou sbalené.
 * `waiting` je záměrně mezi rozbalenými — jinak by skupina mezi dvěma kroky problikávala.
 */
export function defaultCollapsed(visual: GroupVisualStatus): boolean {
  return visual !== 'running' && visual !== 'waiting' && visual !== 'error';
}

/** Ruční přepnutí sbalení uživatelem — platí jen dokud se nezmění automatický stav skupiny. */
export interface CollapseOverride {
  collapsed: boolean;
  /** Hodnota `defaultCollapsed` v okamžiku kliknutí; při jejím posunu override zaniká. */
  auto: boolean;
}

/**
 * Postaví ploché řádky stromu z kroků běhu. `overrides` je mapa ručních přepnutí (klíč řádku →
 * stav); bez přepnutí rozhoduje `defaultCollapsed` ze stavu skupiny, takže dokončená skupina se
 * sama sbalí a rozběhnutá/chybová sama rozbalí. Ruční přepnutí přebíjí automatiku jen do nejbližší
 * změny automatického stavu (např. skupina doběhne → sbalí se i po ručním rozbalení).
 */
export function buildTreeRows(
  runId: string,
  steps: readonly JobStepDto[],
  overrides: Record<string, CollapseOverride>
): JobTreeRow[] {
  const byId = new Map(steps.map((s) => [s.id, s]));
  const childrenByParent = new Map<string | null, JobStepDto[]>();
  for (const step of steps) {
    const parentKey = step.parentStepId ?? null;
    const list = childrenByParent.get(parentKey);
    if (list) list.push(step);
    else childrenByParent.set(parentKey, [step]);
  }
  for (const list of childrenByParent.values()) {
    list.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  const leafDescendantsCache = new Map<string, JobStepDto[]>();
  const leafDescendants = (group: JobStepDto): JobStepDto[] => {
    const cached = leafDescendantsCache.get(group.id);
    if (cached) return cached;
    const leaves: JobStepDto[] = [];
    for (const child of childrenByParent.get(group.id) ?? []) {
      if (child.isGroup) leaves.push(...leafDescendants(child));
      else leaves.push(child);
    }
    leafDescendantsCache.set(group.id, leaves);
    return leaves;
  };

  const rows: JobTreeRow[] = [];

  const leafWaitInfo = (leaf: JobStepDto): LeafWaitInfo | null => {
    if (stepVisualStatus(leaf.status) !== 'queued') return null;
    if (leaf.status === 'Ready') return { kind: 'slot' };
    const names = [
      ...new Set(
        leaf.dependsOnStepIds
          .map((id) => byId.get(id)?.name)
          .filter((name): name is string => name !== undefined)
      ),
    ];
    if (names.length === 0) return { kind: 'slot' };
    return { kind: 'dependencies', names: names.slice(0, 2), more: Math.max(names.length - 2, 0) };
  };

  const emitLeaf = (
    leaf: JobStepDto,
    isLast: boolean,
    guides: boolean[],
    showConnector: boolean
  ) => {
    rows.push({
      kind: 'leaf',
      key: `${runId}:${leaf.id}`,
      guides,
      hasConnector: showConnector,
      connectorContinues: !isLast,
      step: leaf,
      visual: stepVisualStatus(leaf.status),
      wait: leafWaitInfo(leaf),
    });
  };

  const emitGroup = (
    group: JobStepDto,
    isLast: boolean,
    guides: boolean[],
    showConnector: boolean
  ) => {
    const key = `${runId}:${group.id}`;
    const leaves = leafDescendants(group);
    const visual = groupVisualStatus(leaves);
    const auto = defaultCollapsed(visual);
    const isCollapsed = effectiveCollapsed(overrides[key], auto);
    rows.push({
      kind: 'group',
      key,
      guides,
      hasConnector: showConnector,
      connectorContinues: !isLast,
      name: group.name,
      visual,
      collapsed: isCollapsed,
      defaultCollapsed: auto,
      maxParallelism: group.maxParallelism ?? null,
      segments: segmentsFromLeaves(leaves),
      doneCount: leaves.filter((l) => stepVisualStatus(l.status) === 'done').length,
      totalCount: leaves.length,
    });
    if (!isCollapsed) {
      walkChildren(group.id, showConnector ? [...guides, !isLast] : [], true);
    }
  };

  const emitChunk = (
    parentId: string | null,
    chunkIndex: number,
    items: JobStepDto[],
    startIndex: number,
    isLast: boolean,
    guides: boolean[],
    showConnector: boolean
  ) => {
    const key = `${runId}:${parentId ?? 'root'}:chunk${chunkIndex}`;
    const leaves = items.flatMap((item) => (item.isGroup ? leafDescendants(item) : [item]));
    // Chunky se řídí stejnou automatikou jako skutečné skupiny — otevřený je jen ten rozpracovaný.
    const visual = groupVisualStatus(leaves);
    const auto = defaultCollapsed(visual);
    const isCollapsed = effectiveCollapsed(overrides[key], auto);
    rows.push({
      kind: 'chunk',
      key,
      guides,
      hasConnector: showConnector,
      connectorContinues: !isLast,
      name: '',
      visual,
      collapsed: isCollapsed,
      defaultCollapsed: auto,
      maxParallelism: null,
      segments: segmentsFromLeaves(leaves),
      doneCount: leaves.filter((l) => stepVisualStatus(l.status) === 'done').length,
      totalCount: leaves.length,
      chunkRange: { from: startIndex + 1, to: startIndex + items.length },
    });
    if (!isCollapsed) {
      const childGuides = showConnector ? [...guides, !isLast] : [];
      items.forEach((item, i) => emitNode(item, i === items.length - 1, childGuides, true));
    }
  };

  const emitNode = (
    node: JobStepDto,
    isLast: boolean,
    guides: boolean[],
    showConnector: boolean
  ) =>
    node.isGroup
      ? emitGroup(node, isLast, guides, showConnector)
      : emitLeaf(node, isLast, guides, showConnector);

  const walkChildren = (parentId: string | null, guides: boolean[], showConnector: boolean) => {
    const children = childrenByParent.get(parentId) ?? [];
    if (children.length > CHUNK_THRESHOLD) {
      const chunkCount = Math.ceil(children.length / CHUNK_SIZE);
      for (let c = 0; c < chunkCount; c++) {
        const start = c * CHUNK_SIZE;
        const items = children.slice(start, start + CHUNK_SIZE);
        emitChunk(parentId, c, items, start, c === chunkCount - 1, guides, showConnector);
      }
      return;
    }
    children.forEach((child, i) =>
      emitNode(child, i === children.length - 1, guides, showConnector)
    );
  };

  walkChildren(null, [], false);
  return rows;
}

/** Ruční přepnutí platí, dokud se nezmění automatický stav skupiny; pak se vrací automatika. */
function effectiveCollapsed(override: CollapseOverride | undefined, auto: boolean): boolean {
  return override && override.auto === auto ? override.collapsed : auto;
}
